/*
 * Shared helpers for compound-widget renderers.
 *
 * Extracted from HeatCircuitTileRenderer so that HeatPumpKpi, HotWaterTile,
 * RoomThermostat, PvSelfConsumption and WeatherTile stay small. HeatCircuit
 * keeps its own copies to avoid touching that file — this module is the
 * canonical version for every renderer built after it.
 */

import { useEffect, useMemo } from 'preact/hooks';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import type { IPointBindingSource, PointQuality } from '../binding';
import { formatNumber } from '../../format/leafValue';

export function usePoint(source: IPointBindingSource, id: string) {
  const sig = useMemo(() => pointSignal(source, id), [source, id]);
  useEffect(() => sig.release, [sig]);
  return sig;
}

export function useOptionalPoint(source: IPointBindingSource, id: string | undefined) {
  const sig = useMemo(() => (id ? pointSignal(source, id) : null), [source, id]);
  useEffect(() => sig?.release ?? (() => {}), [sig]);
  return sig;
}

export function numeric(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export type QualityBucket = 'ok' | 'stale' | 'bad';

/**
 * Grobe Einordnung fuer die Kachel-Dekoration der Compound-Widgets.
 *
 * Der Default-Zweig ist der gefaehrliche: `PointQuality` hat sechs Werte,
 * nicht vier (`disconnected` und `override` kamen 2026-09-03 dazu). Ein
 * "alles ausser bad/stale ist ok" haette einen geforcten oder einen bei
 * abgerissenem Transport zuletzt gesehenen Wert wie eine frische Messung
 * gerendert. Deshalb entscheidet `isDegraded` — eine Stelle, die mit dem
 * Alphabet mitwaechst.
 */
export function quality(pv: { quality: PointQuality } | undefined | null): QualityBucket {
  if (!pv) return 'stale';
  if (pv.quality === 'bad' || pv.quality === 'disconnected') return 'bad';
  return isDegraded(pv) ? 'stale' : 'ok';
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function coerceBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'on' || s === '1' || s === 'open' || s === 'offen') return true;
    if (s === 'false' || s === 'off' || s === '0' || s === 'closed' || s === 'geschlossen' || s === 'zu') return false;
  }
  return null;
}

/** Formats a raw power value (watts) as "1.23 kW" or "234 W" depending on
 *  magnitude. Returns null-safe placeholder when the input is null. */
export function formatPower(watts: number | null): { text: string; unit: string } {
  if (watts == null) return { text: '—', unit: 'kW' };
  const abs = Math.abs(watts);
  if (abs >= 1000) return { text: formatNumber(watts / 1000, 2), unit: 'kW' };
  return { text: formatNumber(Math.round(watts), 0), unit: 'W' };
}

/** Renders "12h 34m" from a numeric minutes / hours value.
 *  Convention: values > 1000 are treated as minutes, else as hours with a
 *  fractional part. Robust to either wire choice from the controller. */
export function formatRuntimeToday(v: number | null): string {
  if (v == null) return '—';
  const minutes = v > 1000 ? Math.round(v) : Math.round(v * 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

/** Formats a lifetime runtime value as "15320 h". */
export function formatRuntimeLifetime(v: number | null): string {
  if (v == null) return '—';
  return `${formatNumber(Math.round(v), 0)} h`;
}

/** Coarse "in X Tagen" / "vor X Tagen" formatter for ISO date strings.
 *  Returns null when the input is not parseable. */
export function formatRelativeDays(iso: unknown): string | null {
  if (typeof iso !== 'string') return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  const deltaMs = parsed - Date.now();
  const deltaDays = Math.round(deltaMs / 86_400_000);
  if (deltaDays === 0) return 'heute';
  if (deltaDays === 1) return 'morgen';
  if (deltaDays === -1) return 'gestern';
  if (deltaDays > 1) return `in ${deltaDays} Tagen`;
  return `vor ${Math.abs(deltaDays)} Tagen`;
}
